import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { INPUT_CLS, LABEL_CLS } from '@/components/brand/BrandKit';

const ASIDE_IMG = '/generated/personal-keys-handover.jpg';
const PERKS = [
  'Faster chauffeur, transfer and coach bookings',
  'Corporate quotes, invoices and account management',
  'One login across Europcar, Goldcar and Eurogulf brands',
];

export function startGoogleSignIn() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + '/account';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function Field({ label, htmlFor, children, hint }) {
  return (
    <div>
      <label htmlFor={htmlFor} className={LABEL_CLS}>{label}</label>
      {children}
      {hint && <p className="mt-1.5 font-body text-xs text-[#888]">{hint}</p>}
    </div>
  );
}

export function PasswordField({ id, testid, value, onChange, placeholder = 'Enter password', autoComplete = 'current-password' }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input id={id} data-testid={testid} type={show ? 'text' : 'password'} value={value} onChange={onChange} placeholder={placeholder} autoComplete={autoComplete} className={`${INPUT_CLS} pr-12`} required />
      <button type="button" data-testid={`${testid}-toggle`} onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide password' : 'Show password'}
        className="absolute right-2 top-1/2 -translate-y-1/2 grid h-9 w-9 place-items-center rounded-md text-[#888] hover:text-[#EE5A01] hover:bg-[#FFF4ED]">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z" />
      <path fill="#FBBC05" d="M10.5 28.6A14.6 14.6 0 0 1 9.5 24c0-1.6.3-3.2.8-4.6l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.9-6.1z" />
      <path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.5-5.8c-2.1 1.4-4.8 2.3-8.1 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
    </svg>
  );
}

export function GoogleButton({ testid, label = 'Continue with Google' }) {
  return (
    <button type="button" data-testid={testid} onClick={startGoogleSignIn}
      className="w-full inline-flex min-h-[48px] items-center justify-center gap-3 rounded-full border border-black/15 bg-white px-5 font-heading font-bold text-sm text-[#121212] hover:border-[#121212] hover:bg-[#FAFAFA] transition-colors">
      <GoogleMark /> {label}
    </button>
  );
}

export function OrDivider() {
  return (
    <div className="flex items-center gap-4 my-6" aria-hidden="true">
      <span className="h-px flex-1 bg-black/10" />
      <span className="font-heading text-[11px] font-bold tracking-[0.15em] uppercase text-[#999]">or</span>
      <span className="h-px flex-1 bg-black/10" />
    </div>
  );
}

function Aside() {
  return (
    <aside className="relative hidden lg:block overflow-hidden">
      <img src={ASIDE_IMG} alt="Handing over the keys at a Eurogulf branch" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.25)_0%,rgba(0,0,0,0.75)_100%)]" />
      <div className="relative z-10 flex h-full flex-col justify-end p-12 xl:p-16">
        <span className="chip-orange w-fit">My Account</span>
        <h2 className="mt-5 font-heading font-black text-4xl xl:text-5xl text-white leading-[1.05]">One login.<br />Every way you move.</h2>
        <ul className="mt-8 space-y-3">
          {PERKS.map((p) => (
            <li key={p} className="flex items-start gap-3 font-body text-sm text-white/85">
              <span className="mt-0.5 grid h-5 w-5 flex-shrink-0 place-items-center rounded-full bg-[#EE5A01] text-white"><Check className="w-3 h-3" /></span>{p}
            </li>
          ))}
        </ul>
        <p className="mt-10 font-body text-xs text-white/60">Eurogulf Mobility Group · Since 1976 · WE MOVE YOU!</p>
      </div>
    </aside>
  );
}

export function AuthShell({ testid, eyebrow, title, lead, children, footer }) {
  return (
    <div data-testid={testid} className="bg-[#F5F5F3]">
      <div className="grid lg:grid-cols-2 min-h-[calc(100vh-76px)]">
        <Aside />
        <div className="flex items-center justify-center px-4 py-12 sm:px-8 lg:px-12">
          <div className="w-full max-w-md">
            {eyebrow && <span className="chip-outline">{eyebrow}</span>}
            <h1 className="mt-4 font-heading font-black text-3xl sm:text-4xl text-[#121212] leading-tight">{title}</h1>
            {lead && <p className="mt-2 font-body text-sm sm:text-base text-[#666666]">{lead}</p>}
            <div className="mt-8 rounded-2xl border border-black/5 bg-white p-6 sm:p-8 shadow-[0_16px_40px_rgba(0,0,0,0.06)]">{children}</div>
            {footer && <div className="mt-6 text-center font-body text-sm text-[#666666]">{footer}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AuthLink({ to, testid, children }) {
  return <Link to={to} data-testid={testid} className="font-heading font-bold text-[#EE5A01] hover:underline">{children}</Link>;
}
